import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Phone, MessageCircle, RefreshCw, Search, Trash2, Users, Filter } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const headers = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatPrice = (n) =>
  typeof n === 'number' && n > 0 ? `₹${n.toLocaleString('en-IN')}` : '—';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const ActionPill = ({ action }) => {
  const isCall = action === 'call';
  const Icon = isCall ? Phone : MessageCircle;
  const cls = isCall
    ? 'bg-slate-900 text-white'
    : 'bg-green-100 text-green-800 border border-green-200';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${cls}`}
      data-testid={`lead-action-${action}`}
    >
      <Icon className="w-3 h-3" /> {action}
    </span>
  );
};

const UsedCarCustomersManager = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all'); // 'all' | 'call' | 'whatsapp'
  const [dealerFilter, setDealerFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/used-car-leads`, {
        headers: headers(),
      });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast({
        title: 'Failed to load used car leads',
        description: err.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/used-car-leads/${id}`, {
        headers: headers(),
      });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast({ title: 'Lead deleted' });
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err.response?.data?.detail || '',
        variant: 'destructive',
      });
    }
  };

  const dealerOptions = useMemo(() => {
    const map = new Map();
    leads.forEach((l) => {
      if (l.dealer_id) map.set(l.dealer_id, l.dealer_name || l.dealer_slug);
    });
    return Array.from(map.entries());
  }, [leads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (actionFilter !== 'all' && l.action !== actionFilter) return false;
      if (dealerFilter !== 'all' && l.dealer_id !== dealerFilter) return false;
      if (!q) return true;
      const hay = `${l.buyer_name || ''} ${l.buyer_mobile || ''} ${l.dealer_name || ''} ${l.car_make || ''} ${l.car_model || ''} ${l.car_year || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [leads, actionFilter, dealerFilter, search]);

  const callCount = leads.filter((l) => l.action === 'call').length;
  const waCount = leads.filter((l) => l.action === 'whatsapp').length;

  return (
    <Card data-testid="used-car-customers-manager">
      <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Users className="w-6 h-6 text-blue-600" />
            Used Car Customers
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Buyer leads captured every time someone clicks Call or WhatsApp on a dealer microsite.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Total leads</div>
            <div className="text-2xl font-extrabold text-slate-900">{total}</div>
          </div>
          <Button onClick={load} variant="outline" size="sm" data-testid="leads-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Total</div>
            <div className="text-xl font-extrabold text-slate-900">{leads.length}</div>
          </div>
          <div className="rounded-xl border border-green-200 p-3 bg-green-50">
            <div className="text-[11px] uppercase tracking-wider text-green-700 flex items-center gap-1">
              <MessageCircle className="w-3 h-3" /> WhatsApp
            </div>
            <div className="text-xl font-extrabold text-green-800">{waCount}</div>
          </div>
          <div className="rounded-xl border border-slate-300 p-3 bg-slate-100">
            <div className="text-[11px] uppercase tracking-wider text-slate-700 flex items-center gap-1">
              <Phone className="w-3 h-3" /> Call
            </div>
            <div className="text-xl font-extrabold text-slate-900">{callCount}</div>
          </div>
          <div className="rounded-xl border border-blue-200 p-3 bg-blue-50">
            <div className="text-[11px] uppercase tracking-wider text-blue-700">Dealers reached</div>
            <div className="text-xl font-extrabold text-blue-900">{dealerOptions.length}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, mobile, dealer, car…"
              className="pl-9"
              data-testid="leads-search"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="border border-slate-200 rounded-md px-2.5 py-2 text-sm bg-white"
              data-testid="leads-action-filter"
            >
              <option value="all">All actions</option>
              <option value="whatsapp">WhatsApp only</option>
              <option value="call">Calls only</option>
            </select>
          </div>
          <select
            value={dealerFilter}
            onChange={(e) => setDealerFilter(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-2 text-sm bg-white"
            data-testid="leads-dealer-filter"
          >
            <option value="all">All dealers</option>
            {dealerOptions.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-500 border border-dashed border-slate-200 rounded-xl">
            <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="font-semibold">No used car leads yet</p>
            <p className="text-sm">Leads will show up here whenever a buyer hits Call or WhatsApp on a dealer site.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                  <th className="text-left px-4 py-3 font-semibold">Buyer</th>
                  <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                  <th className="text-left px-4 py-3 font-semibold">Dealer</th>
                  <th className="text-left px-4 py-3 font-semibold">Car</th>
                  <th className="text-left px-4 py-3 font-semibold">Price</th>
                  <th className="text-left px-4 py-3 font-semibold">Action</th>
                  <th className="px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((l) => {
                  const car = [l.car_year, l.car_make, l.car_model].filter(Boolean).join(' ') || '—';
                  return (
                    <tr key={l.id} className="hover:bg-slate-50/60" data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(l.created_at)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{l.buyer_name}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                        <a href={`tel:${l.buyer_mobile}`} className="hover:text-blue-600">+91 {l.buyer_mobile}</a>
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`/dealers/${l.dealer_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {l.dealer_name}
                        </a>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{car}</td>
                      <td className="px-4 py-3 text-slate-700">{formatPrice(l.car_price)}</td>
                      <td className="px-4 py-3"><ActionPill action={l.action} /></td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(l.id)}
                          data-testid={`lead-delete-${l.id}`}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UsedCarCustomersManager;
